import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { MapPin, Save, RotateCcw, Layers, Target, Pencil } from 'lucide-react';
import 'leaflet/dist/leaflet.css';

const TILE_LAYERS = {
  street: {
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; OSM',
    label: 'Street',
  },
  satellite: {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: '&copy; Esri',
    label: 'Satellite',
  },
};

export default function WellLocationPicker({
  coordinates = null,
  wellMarker = null,
  address = '',
  onSave = () => {},
  readOnly = false,
  parcelId = '',
  county = '',
  compact = false,
  savedBoundary = [],
  onBoundaryLoaded = () => {},
}) {
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const tileLayerRef = useRef(null);
  const boundaryLayersRef = useRef([]);
  const [markerPosition, setMarkerPosition] = useState(wellMarker);
  const [tileType, setTileType] = useState('satellite');
  const [mapReady, setMapReady] = useState(false);
  // Seeded from the boundary already saved on the estimate (if any) so the
  // parcel outline draws INSTANTLY and stays visible even if the live
  // FDOR/SWFWMD lookup below is slow or briefly unavailable (Alian: "primero
  // me salio el boundary... pero ya dejo de salir" — it was flickering
  // because a failed/empty live fetch was wiping out a boundary that had
  // drawn fine moments before).
  const [boundary, setBoundary] = useState(savedBoundary || []);
  // Soft lock: the marker starts locked once it's already saved (so reopening an
  // estimate never lets the pin move by accident). Press "Edit" to unlock it again.
  // `readOnly` (hard prop) always wins and never shows the Edit button at all.
  const [locked, setLocked] = useState(!!wellMarker);
  const isLocked = readOnly || locked;
  const lockedRef = useRef(isLocked);
  useEffect(() => { lockedRef.current = isLocked; }, [isLocked]);
  useEffect(() => { setLocked(!!wellMarker); }, [coordinates?.lat, coordinates?.lng]); // eslint-disable-line react-hooks/exhaustive-deps

  const center = coordinates || { lat: 27.4958, lng: -81.4411 };

  // If the saved boundary arrives/changes (e.g. switching estimates) and we
  // don't already have one drawn, seed it right away — never overwrites a
  // boundary that's already showing.
  useEffect(() => {
    if ((!boundary || boundary.length === 0) && savedBoundary && savedBoundary.length > 0) {
      setBoundary(savedBoundary);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedBoundary]);

  // Fetch the parcel boundary (property line) so it can be drawn on top of the
  // satellite imagery, same as the county Property Appraiser map. A failed or
  // empty response NEVER clears an already-drawn boundary — the FDOR/SWFWMD
  // source is intermittent, but once we have a good polygon it should stay
  // stable on screen (persisted back to the estimate via onBoundaryLoaded).
  useEffect(() => {
    if (!parcelId && !(coordinates?.lat && coordinates?.lng)) return;
    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams();
        if (parcelId) params.set('parcel_id', parcelId);
        if (coordinates?.lat && coordinates?.lng) {
          params.set('lat', coordinates.lat);
          params.set('lng', coordinates.lng);
        }
        if (county) params.set('county', county);
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/property/boundary?${params.toString()}`);
        const data = await res.json();
        if (!cancelled && data.boundary && data.boundary.length > 0) {
          setBoundary(data.boundary);
          onBoundaryLoaded(data.boundary);
        }
      } catch {
        // Network/timeout — keep whatever boundary is already showing.
      }
    })();
    return () => { cancelled = true; };
  }, [parcelId, county, coordinates?.lat, coordinates?.lng]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initialize map
  useEffect(() => {
    if (!mapContainerRef.current) return;
    let map;

    const init = async () => {
      const L = await import('leaflet');

      // Cleanup previous
      if (mapInstanceRef.current) {
        try { mapInstanceRef.current.remove(); } catch {}
        mapInstanceRef.current = null;
      }
      if (mapContainerRef.current._leaflet_id) {
        delete mapContainerRef.current._leaflet_id;
      }
      if (!mapContainerRef.current) return;

      map = L.map(mapContainerRef.current, {
        fadeAnimation: false,
        zoomAnimation: true,
        zoomControl: true,
        // Alian's video: hovering the mouse over the map was zooming it and
        // scrolling the whole page. Disable scroll-wheel zoom — zoom stays
        // available via the +/- buttons (zoomControl) or double-click.
        scrollWheelZoom: false,
      }).setView([center.lat, center.lng], 18);
      mapInstanceRef.current = map;

      // Add tile layer
      const tile = TILE_LAYERS[tileType];
      tileLayerRef.current = L.tileLayer(tile.url, { attribution: tile.attribution, maxZoom: 20 }).addTo(map);

      // Property marker (red pin for the address)
      const propertyIcon = L.divIcon({
        html: `<div style="background:#dc2626;width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3)">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
        </div>`,
        iconSize: [28, 28],
        iconAnchor: [14, 14],
        className: '',
      });
      L.marker([center.lat, center.lng], { icon: propertyIcon })
        .addTo(map)
        .bindPopup(`<strong>Job Site</strong><br/>${address || 'Property location'}`);

      // If there's an existing well marker, place it
      if (wellMarker?.lat && wellMarker?.lng) {
        placeWellMarker(L, map, wellMarker.lat, wellMarker.lng);
      }

      // Click to place marker — checks the CURRENT lock state via ref (locked
      // can toggle after the map is created, e.g. via the "Edit" button).
      map.on('click', (e) => {
        if (lockedRef.current) return;
        placeWellMarker(L, map, e.latlng.lat, e.latlng.lng);
      });

      // North indicator + compass
      const compassDiv = L.control({ position: 'topright' });
      compassDiv.onAdd = () => {
        const div = L.DomUtil.create('div');
        div.innerHTML = `<div style="background:white;border-radius:8px;padding:6px 10px;box-shadow:0 2px 8px rgba(0,0,0,0.15);display:flex;align-items:center;gap:4px;font-size:12px;font-weight:600;color:#003366">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#003366" stroke-width="2"><polygon points="12 2 19 21 12 17 5 21 12 2"/></svg>
          N
        </div>`;
        return div;
      };
      compassDiv.addTo(map);

      setTimeout(() => {
        if (map && mapContainerRef.current) map.invalidateSize();
      }, 200);
      setMapReady(true);
    };

    init();

    return () => {
      if (mapInstanceRef.current) {
        try { mapInstanceRef.current.remove(); } catch {}
        mapInstanceRef.current = null;
      }
    };
  }, [center.lat, center.lng]); // eslint-disable-line react-hooks/exhaustive-deps

  // Draw/redraw the parcel boundary polygon whenever it (re)loads or the map re-inits
  useEffect(() => {
    if (!mapReady || !mapInstanceRef.current) return;
    let cancelled = false;
    (async () => {
      const L = await import('leaflet');
      if (cancelled) return;
      const map = mapInstanceRef.current;
      boundaryLayersRef.current.forEach(layer => { try { map.removeLayer(layer); } catch {} });
      boundaryLayersRef.current = [];
      const allBounds = [];
      (boundary || []).forEach(ring => {
        if (!ring || ring.length < 3) return;
        const poly = L.polygon(ring, {
          color: '#2563eb',
          weight: 2.5,
          fillColor: '#2563eb',
          fillOpacity: 0.06,
          dashArray: '6 4',
        }).addTo(map);
        poly.bindTooltip('Parcel boundary', { sticky: true });
        boundaryLayersRef.current.push(poly);
        allBounds.push(poly.getBounds());
      });
      // Fit the map to the parcel outline (Alian: "que haga los boundaries del
      // grid de la propiedad") so the whole property line is visible, not just
      // a tight zoom on the well pin.
      if (allBounds.length) {
        let combined = allBounds[0];
        allBounds.slice(1).forEach(b => { combined = combined.extend(b); });
        map.fitBounds(combined.pad(0.15));
      }
    })();
    return () => { cancelled = true; };
  }, [boundary, mapReady]);

  // Place/move the well marker (blue circle)
  const placeWellMarker = useCallback(async (L, map, lat, lng) => {
    // Remove previous
    if (markerRef.current) {
      try { map.removeLayer(markerRef.current); } catch {}
    }

    const wellIcon = L.divIcon({
      html: `<div style="position:relative">
        <div style="width:36px;height:36px;border-radius:50%;border:3px solid #2563eb;background:rgba(37,99,235,0.2);display:flex;align-items:center;justify-content:center">
          <div style="width:12px;height:12px;border-radius:50%;background:#2563eb;box-shadow:0 0 0 3px rgba(37,99,235,0.3)"></div>
        </div>
        <div style="position:absolute;top:-18px;left:50%;transform:translateX(-50%);background:#2563eb;color:white;font-size:9px;font-weight:700;padding:2px 6px;border-radius:4px;white-space:nowrap;letter-spacing:0.5px">WELL</div>
      </div>`,
      iconSize: [36, 36],
      iconAnchor: [18, 18],
      className: '',
    });

    const marker = L.marker([lat, lng], {
      icon: wellIcon,
      draggable: !lockedRef.current,
    }).addTo(map);

    marker.bindPopup(
      `<div style="text-align:center"><strong style="color:#2563eb">Well Location</strong><br/><span style="font-size:11px">${lat.toFixed(6)}, ${lng.toFixed(6)}</span></div>`
    );

    marker.on('dragend', (e) => {
      if (lockedRef.current) return;
      const pos = e.target.getLatLng();
      setMarkerPosition({ lat: pos.lat, lng: pos.lng });
      marker.setPopupContent(
        `<div style="text-align:center"><strong style="color:#2563eb">Well Location</strong><br/><span style="font-size:11px">${pos.lat.toFixed(6)}, ${pos.lng.toFixed(6)}</span></div>`
      );
    });

    markerRef.current = marker;
    setMarkerPosition({ lat, lng });
  }, []);

  // Keep the marker's own drag-ability in sync with the lock state (so clicking
  // "Edit" unlocks an already-placed marker without needing to re-click the map).
  useEffect(() => {
    if (markerRef.current?.dragging) {
      if (isLocked) markerRef.current.dragging.disable();
      else markerRef.current.dragging.enable();
    }
  }, [isLocked]);

  // Toggle tile layer
  const toggleTile = async () => {
    const newType = tileType === 'street' ? 'satellite' : 'street';
    setTileType(newType);
    if (mapInstanceRef.current && tileLayerRef.current) {
      const L = await import('leaflet');
      mapInstanceRef.current.removeLayer(tileLayerRef.current);
      const tile = TILE_LAYERS[newType];
      tileLayerRef.current = L.tileLayer(tile.url, { attribution: tile.attribution, maxZoom: 20 }).addTo(mapInstanceRef.current);
    }
  };

  // Clear marker
  const clearMarker = async () => {
    if (markerRef.current && mapInstanceRef.current) {
      try { mapInstanceRef.current.removeLayer(markerRef.current); } catch {}
      markerRef.current = null;
    }
    setMarkerPosition(null);
  };

  // Re-center map
  const reCenter = () => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.setView([center.lat, center.lng], 18);
    }
  };

  const handleSave = () => {
    onSave(markerPosition);
    setLocked(true);
  };

  return (
    <div className="space-y-3" data-testid="well-location-picker">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
            <Target className="w-4 h-4 text-white" />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900">Well Location</p>
            <p className="text-[11px] text-gray-500">
              {isLocked ? 'Saved well position — locked' : 'Click on the map to mark the exact well location'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={toggleTile}
            className="h-7 text-[11px] gap-1"
            data-testid="well-map-toggle-tile"
          >
            <Layers className="w-3 h-3" />
            {tileType === 'street' ? 'Satellite' : 'Street'}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={reCenter}
            className="h-7 text-[11px] gap-1"
          >
            <Target className="w-3 h-3" />
          </Button>
          {isLocked && !readOnly && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setLocked(false)}
              className="h-7 text-[11px] gap-1 text-blue-600 border-blue-200 hover:bg-blue-50"
              data-testid="well-marker-edit"
            >
              <Pencil className="w-3 h-3" />
              Edit
            </Button>
          )}
        </div>
      </div>

      {/* Map */}
      <div className="relative rounded-xl overflow-hidden border border-gray-200 shadow-sm">
        <div
          ref={mapContainerRef}
          className={compact ? 'h-[200px] w-full bg-gray-100' : 'h-[320px] w-full bg-gray-100'}
          style={{ zIndex: 1, cursor: isLocked ? 'default' : 'crosshair' }}
          data-testid="well-location-map"
        />

        {/* Legend overlay */}
        <div className="absolute bottom-2 left-2 z-[400] flex gap-2">
          <div className="bg-white/90 backdrop-blur-sm rounded-md px-2 py-1 flex items-center gap-1.5 text-[10px] shadow">
            <div className="w-3 h-3 rounded-full bg-red-500 border border-white" />
            <span className="text-gray-600">Property</span>
          </div>
          <div className="bg-white/90 backdrop-blur-sm rounded-md px-2 py-1 flex items-center gap-1.5 text-[10px] shadow">
            <div className="w-3 h-3 rounded-full border-2 border-blue-500 bg-blue-200" />
            <span className="text-gray-600">Well</span>
          </div>
          {boundary.length > 0 && (
            <div className="bg-white/90 backdrop-blur-sm rounded-md px-2 py-1 flex items-center gap-1.5 text-[10px] shadow" data-testid="well-map-boundary-legend">
              <div className="w-3 h-0 border-t-2 border-dashed border-blue-600" />
              <span className="text-gray-600">Parcel boundary</span>
            </div>
          )}
        </div>
      </div>

      {/* Marker info & actions */}
      {markerPosition ? (
        <div className="flex items-center justify-between p-2.5 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-blue-600" />
            <div>
              <p className="text-xs font-medium text-blue-800">Well marked</p>
              <p className="text-[10px] text-blue-600 font-mono">
                {markerPosition.lat.toFixed(6)}, {markerPosition.lng.toFixed(6)}
              </p>
            </div>
          </div>
          {!isLocked && (
            <div className="flex items-center gap-1.5">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={clearMarker}
                className="h-7 text-[11px] gap-1 text-red-600 border-red-200 hover:bg-red-50"
                data-testid="well-marker-clear"
              >
                <RotateCcw className="w-3 h-3" />
                Clear
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={handleSave}
                className="h-7 text-[11px] gap-1 bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="well-marker-save"
              >
                <Save className="w-3 h-3" />
                Save Location
              </Button>
            </div>
          )}
        </div>
      ) : !isLocked ? (
        <div className="p-2.5 bg-gray-50 border border-dashed border-gray-300 rounded-lg text-center">
          <p className="text-xs text-gray-500">
            Click on the map to mark where the well will be drilled.
            You can drag the marker to adjust.
          </p>
        </div>
      ) : null}
    </div>
  );
}
